import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  computed,
  ViewChild,
  ChangeDetectionStrategy,
  model,
} from "@angular/core";

import { animate, style, transition, trigger } from "@angular/animations";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, map } from "rxjs";

@Component({
    selector: "carousel",
    templateUrl: "./carousel.component.html",
    styleUrls: ["./carousel.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-5px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-5px)' })),
      ]),
    ]),
  ],
})
export class CarouselComponent implements AfterViewInit, OnInit {

  public dataSource = input([]);
  public showSelection = input<boolean>();
  public showCount = input<boolean>();
  public enableHover = input(true);
  public shouldAddBreadcrumb = input(true);
  public revealOnHover = input(false);
  
  public isEmpty = computed(() => this.dataSource().length === 0);
  public selectedId = signal<string>('');
  public showActions = model(false);

  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);

  @ViewChild("sliderContent", { static: false }) 
  private sliderContent!: ElementRef;

  private itemsPerSlide = signal(6);
  private currentIndex = signal(0);

  public totalPages = computed(() => Math.ceil(this.dataSource().length / this.itemsPerSlide()));
  public currentPage = computed(() => Math.floor(this.currentIndex() / this.itemsPerSlide()));

  public pagesArray = computed(() => {
    return Array.from({ length: this.totalPages() }, (_, i) => i);
  });


  public select(id: string): void {
    this.selectedId.set(id);
  }

  public virtualItems = computed(() => {
    const totalItems = this.dataSource().length;
    const itemsPerSlide = this.itemsPerSlide();
    

    const bufferSize = itemsPerSlide * 20;
    
    // Calculate the range of items to render with minimal buffering
    const startIndex = Math.max(0, this.currentIndex() - bufferSize);
    const endIndex = Math.min(
      totalItems,
      this.currentIndex() + itemsPerSlide + bufferSize
    );
    
    // Create the virtual items with their original indices
    return this.dataSource().slice(startIndex, endIndex).map((item, index) => ({
      data: item,
      originalIndex: startIndex + index,
      virtualIndex: index
    }));
  });

  private preloadAdjacentItems(): void {
    const virtualItems = this.virtualItems();
    if (virtualItems.length === 0) return;
    
    const firstVirtualIdx = virtualItems[0].originalIndex;
    const lastVirtualIdx = virtualItems[virtualItems.length - 1].originalIndex;
    const itemsPerSlide = this.itemsPerSlide();
    const currentIdx = this.currentIndex();
    
    // If we're getting close to the edge of our virtual range, trigger a recalculation
    const approachingStart = currentIdx - firstVirtualIdx < itemsPerSlide;
    const approachingEnd = lastVirtualIdx - (currentIdx + itemsPerSlide) < itemsPerSlide;
    
    if (approachingStart || approachingEnd) {
      // Force recalculation of virtual items via signal update
      // This will cause Angular to detect the change and update the DOM
      this.currentIndex.set(this.currentIndex());
    }
  }

  public canGoNext = computed(() => {
    return this.currentIndex() + this.itemsPerSlide() < this.dataSource().length;
  });

  public canGoPrev = computed(() => {
    return this.currentIndex() > 0;
  });

  // Calculate item width based on items per slide
  public itemWidth = computed(() => {
    return 100 / this.itemsPerSlide();
  });

  ngOnInit(): void {
    this.setResponsiveItemsPerSlide();
  }

  ngAfterViewInit(): void {
    this.updateCarousel();
  }

  private updateCarousel(): void {
    if (!this.sliderContent) return;
    
    // Use requestAnimationFrame to ensure DOM updates have occurred
    requestAnimationFrame(() => {
      this.updateSlidePosition();
    });
  }


  private updateSlidePosition(): void {
    if (!this.sliderContent) return;
    
    const slider = this.sliderContent.nativeElement as HTMLElement;
    const itemsPerSlide = this.itemsPerSlide();
    
    // Calculate the item width as a percentage
    const itemWidthPercent = 100 / itemsPerSlide;
    
    // Calculate the translation percentage
    const translatePercent = this.currentIndex() * itemWidthPercent;
    
    // Apply the translation with transition
    slider.style.transition = "transform 0.7s ease-in-out";
    slider.style.transform = `translate3d(-${translatePercent}%, 0, 0)`;
  }

  public itemStyle = computed(() => {
    return {
      width: `calc(${100 / this.itemsPerSlide()}% - 1.5rem)`,
      'min-width': `calc(${100 / this.itemsPerSlide()}% - 1.5rem)`,
      'margin-right': '1.5rem'
    };
  });

  private adjustCurrentIndex(): void {
    const items = this.itemsPerSlide();
    const maxIndex = Math.max(0, this.dataSource().length - items);
    
    if (this.currentIndex() > maxIndex) {
      this.currentIndex.set(Math.floor(maxIndex / items) * items);
    }
  }

public next(): void {
  if (this.currentIndex() + this.itemsPerSlide() >= this.dataSource().length) return;

  this.currentIndex.set(this.currentIndex() + this.itemsPerSlide());
  this.preloadAdjacentItems();
  
  requestAnimationFrame(() => {
    this.updateSlidePosition();
  });
}

public prev(): void {
  if (this.currentIndex() === 0) return;

  this.currentIndex.set(this.currentIndex() - this.itemsPerSlide());
  this.preloadAdjacentItems();
  
  requestAnimationFrame(() => {
    this.updateSlidePosition();
  });
}

  public goToPage(pageIndex: number): void {
    this.currentIndex.set(pageIndex * this.itemsPerSlide());
    
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      this.updateSlidePosition();
    });
  }

  public isVisible(originalIndex: number): boolean {
    const start = this.currentIndex();
    const end = start + this.itemsPerSlide();
    return originalIndex >= start && originalIndex < end;
  }

  public trackByFn(index: number, item: any): number {
    return item.originalIndex;
  }

  private setResponsiveItemsPerSlide(): void {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(result => 
          result.breakpoints[Breakpoints.XSmall] ? 2 :
          result.breakpoints[Breakpoints.Small] ? 4 :
          result.breakpoints[Breakpoints.Medium] ? 6 : 8
        ),
        distinctUntilChanged() // Only emit when the value changes
      )
      .subscribe(newValue => {
        this.itemsPerSlide.set(newValue);
        this.adjustCurrentIndex();
        
        // Use requestAnimationFrame instead of setTimeout for better performance
        // This ensures the DOM has been updated before we try to manipulate it
        requestAnimationFrame(() => {
          this.updateCarousel();
        });
      });
  }

  public toggleActions() {
    this.showActions.update(val => !val);
  }

  public onHeaderMouseEnter() {
    this.setActionsVisibleOnHover(true);
  }

  public onHeaderMouseLeave() {
    this.setActionsVisibleOnHover(false);
  }

  private setActionsVisibleOnHover(visible: boolean) {
    if (this.revealOnHover()) {
      this.showActions.set(visible);
    }
  }
}